import Vue from 'vue';
import Vuex from 'vuex';
import {VueRouter} from "vue-router/types/router";
import {ProgramPaths} from "@/enum/ProgramPaths";
import {PersonelEntity} from "@/entity/PersonelEntity";
import gorevYoneticisi from "@/plugins/uyap-plugin/store/modules/GorevYoneticisi";
import takipDetay from "@/store/modules/takipDetay";
import takipEkle from "@/store/modules/takipEkle";
import theme from "@/store/modules/theme";
import app from "@/main";
import {AppState} from "@/types";
import VueSocketIOExt from 'vue-socket.io-extended';
import {io} from 'socket.io-client';
import takipHazirla from '@/store/modules/formWizards/takipHazirla';
import mtsTakipHazirla from '@/store/modules/formWizards/mtsTakipHazirla';
import {KullaniciBilgileriCevap} from "@/plugins/uyap-plugin/uyap/KullaniciBilgileri";
import {sortMahkemelerByIsimAsc} from "@/utils/mahkemeSort";
import {offerFromLisansDurum} from "@/utils/renewalOffer";
import {demoStatusFromLisansDurum} from "@/utils/demoStatus";
import {tazelemeGecikmesi} from "@/utils/lisansKisit";
import _swal from "sweetalert2";

// İmza websocket
const socket = io('https://imza.eicrapro.com:3000', {
    autoConnect: true,
    reconnection: true
});
Vue.use(VueSocketIOExt, socket);
Vue.use(Vuex);
// DEV-2113 — /ben kural özetinin (ozellik_durumu) tazelenmesi. Sekme başına tek
// zamanlayıcı, aynı anda tek istek (bkz. lisansKisit.ts -> tazelemeGecikmesi).
let ozellikTazelemeZamanlayici: ReturnType<typeof setTimeout> | null = null;
let ozellikTazelemeIstegi: Promise<any> | null = null;
let sonOzellikTazelemesi = 0;

export const store = new Vuex.Store({
    modules: {
        // @ts-ignore
        gorevYoneticisi,
        takipDetay,
        takipEkle,
        theme,
        takipHazirla,
        mtsTakipHazirla
    },
    state: <AppState>{
        accessToken: localStorage.getItem('accessToken'),
        tenant_id: sessionStorage.getItem('tenantId'),
        user: <PersonelEntity>JSON.parse(sessionStorage.getItem('user') ?? "{}"),
        activeProcesses: JSON.parse(sessionStorage.getItem("activeProcesses") ?? "[]"),
        createTcKimlikErrors: [],
        icraProTakipBilgileri : {},
        xml: null,
        eicraproDevMode: false,  // Mock data kullanmak için true yap
        imzaConnection: false,
        uyapConnection: false,
        ssoConfig: null,
        uyapAvukatBilgileri: undefined,
        uyapSorguBakiye: undefined,     // Sorgu bakiyesi
        uyapAvukatBakiye: undefined,    // E-Barobirlik bakiyesi
        avukatlar: [],
        mahkemeler:[],
        noterler:[],
        tenantList: [],
        kurlar: [],
        personeller: [],
        cardList: [],
        tenantListPromise: null,
        imzaConnIntervalId: null,
        ssoUri: process.env.VUE_APP_SSO_TARGET,
        programs: [
            {
                name: "Ofispro",
                programPath: ProgramPaths.ofispro,
                urls: [
                    "ofispro.com",
                    "test.ofispro.com",
                    "ofispro.lcl",
                    "localhost",
                ],
            },
            {
                name: "IcraproWeb",
                programPath: ProgramPaths.icrapro,
                urls: [
                    "icrapro.com",
                    "test.icrapro.com",
                    "cloudbeta.zirve-bilgisayar.com",
                    "cloudopt.zirve-bilgisayar.com",
                    "cloudtest.zirve-bilgisayar.com",
                    "cloudtest1.zirve-bilgisayar.com",
                    "cloudtest2.zirve-bilgisayar.com",
                    "cloudtest3.zirve-bilgisayar.com",
                    "cloudtest4.zirve-bilgisayar.com",
                    "cloudicra.neova.com.tr",
                    "okandemirkan.icrapro.com",
                    "cloud.zirve-bilgisayar.com",
                    "icrapro.lcl",
                ]
            },
            {
                name: "e-İcrapro",
                programPath: ProgramPaths.eicrapro,
                urls: [
                    "eicrapro.com",
                    "test.eicrapro.com",
                    "eicrapro.lcl",
                ]
            }
        ]
    },
    actions: {
        // Yenileme teklifi ve demo durumu için AYRI İSTEK YOKTUR: ikisinin de ham
        // verisi /api/v1/ben yanıtındaki `lisans_durum` alanıyla geliyor ve
        // sessionStorage'daki user ile birlikte saklanıyor. Kademe/gün hesabı
        // renewalOfferFor / demoStatusFor getter'larında yapılır.
        // (Eski uçlar /v1/renewal-offer ve /v1/demo-status geriye dönük uyumluluk
        //  için API'de duruyor; bu uygulama artık çağırmıyor.)

        // Tek kullanımlık otomatik giriş + sepet URL'i alıp mağazaya yönlendirir.
        // product: 'icrapro' | 'eicrapro' — kullanıcının bulunduğu ürüne göre.
        async startRenewalCheckout(_ctx, product: 'icrapro' | 'eicrapro' = 'icrapro') {
            try {
                const result = await Vue.prototype.$http.post('/api/v1/renewal/checkout-link', {product});
                if (result?.url) {
                    window.location.href = result.url;
                } else {
                    app.$toast.error('Yenileme bağlantısı oluşturulamadı.');
                }
            } catch (e) {
                app.$toast.error('Yenileme bağlantısı oluşturulamadı. Lütfen tekrar deneyin.');
            }
        },
        // "Hemen Al": tek kullanımlık otomatik giriş + mağaza fiyatlandırma sayfası
        // URL'i alıp yönlendirir. product: 'icrapro' | 'eicrapro'.
        async startDemoPurchase(_ctx, product: 'icrapro' | 'eicrapro' = 'icrapro') {
            try {
                const result = await Vue.prototype.$http.post('/api/v1/demo/purchase-link', {product});
                if (result?.url) {
                    window.location.href = result.url;
                } else {
                    app.$toast.error('Satın alma bağlantısı oluşturulamadı.');
                }
            } catch (e) {
                app.$toast.error('Satın alma bağlantısı oluşturulamadı. Lütfen tekrar deneyin.');
            }
        },
        async fetchIcraProTakipBilgileri({ commit, state }, id) {
            try {
                const response = await Vue.prototype.$http.get(`/api/v1/takip/${id}`);
                commit('setIcraProTakipBilgileri', response.data);
            } catch (error) {
                console.error('Takip bilgileri alınırken hata oluştu:', error);
            }

        },
        connectToImza({commit}) {
            app.$imza.heartbeat().then(() => {
                commit('setImzaConnection', true);
                app.$toast.success('İmzacı bağlantısı tamamlandı.');
            }).catch((error: any) => {
                commit('setImzaConnection', false);
                app.$toast.error('İmzacı bağlantısı yapılamadı. (' + error + ')');
            });
        },
        startImzaConnInterval({state, commit, dispatch}) {
            if (!state.imzaConnection) {
                const intervalID = setInterval(() => {
                    dispatch('checkUyapSession');
                }, 30000);
                commit('setImzaConnIntervalId', intervalID);
            }
        },
        stopImzaConnInterval({state, commit}) {
            if (state.imzaConnIntervalId) {
                clearInterval(state.imzaConnIntervalId);
                commit('setImzaConnIntervalId', null);
            }
        },
        async checkUyapSession({state, commit, dispatch}) {
            console.log("Check Uyap Session triggered");
            try {
                if (state.eicraproDevMode) {
                    commit('setImzaConnection', true);
                    commit('setUyapConnection', true);
                } else {
                    await app.$imza.heartbeat();
                    // Kimlik ÖNCE gönderilir. Eskiden bu satır kart kontrolünün
                    // arkasındaydı ve kart takılı değilken hiç çalışmıyordu; masaüstü
                    // tarafı TCKN'yi öğrenemediği için sonradan kart takıldığında doğru
                    // kartı seçemiyor, kimlik denetimini de sessizce atlıyordu. TCKN
                    // kartla ilgisiz bir veri ve kart seçimi için ondan ÖNCE gerekli.
                    await app.$imza.setApiCookiesToImza();
                    console.log("SETTEd COOKIES TO IMZA");
                    let cardList = await app.$imza.availableCards();
                    commit('setCardList',cardList);
                    if(cardList.length == 0)
                        return;
                    // TODO(uyap-plugin bump sonrası): .probe() ekle —
                    // await app.$uyap.KullaniciBilgileri().probe().run({});
                    // Bu bir oturum testi; kurtarmaya girerse cevap 48 sn gecikiyor ve
                    // kurtarma kendi yayınladığı connectToUyap'ı bekliyor. probe() bu
                    // checkout'taki plugin sürümünde henüz yok.
                    const avukat = await app.$uyap.KullaniciBilgileri().run({});
                    console.log("KullaniciBilgileri runned");

                    // GÜVENLİK: oturum "geçerli" olması yetmez, DOĞRU KİŞİYE ait olmalı.
                    // Burada denetim hiç yoktu: hesabı A olan kullanıcı, B'nin e-imzasıyla
                    // açılmış oturum üzerinden sorgu yapıp görev üretebiliyor, tevzi
                    // alabiliyordu. Kullanıcı hesabını başkasına verip kullandırabilir.
                    commit('setUyapAvukatBilgileri', avukat);
                    // TODO(uyap-plugin bump sonrası): paylaşılan oturumKimligiUyusuyor()
                    // kullanılsın. Denetim burada inline yazıldı çünkü bu checkout'un
                    // plugin sürümünde OturumKimligi.ts henüz yok ve koruma bugün gerekli.
                    const tcknNormalize = (v: any) => String(v ?? '').replace(/\D/g, '');
                    const oturumTckn = tcknNormalize(avukat?.tcKimlikNo);
                    const hesapTckn = tcknNormalize(sessionStorage.getItem('tckn'));
                    // Bilinmeyen değer uyuşmazlık sayılmaz; eksik veri bağlantıyı düşürmemeli.
                    if (oturumTckn && hesapTckn && oturumTckn !== hesapTckn) {
                        console.error('UYAP oturumu bu hesaba ait değil, bağlantı kabul edilmiyor.');
                        commit('setUyapConnection', false);
                        commit('setImzaConnection', false);
                        _swal.fire({
                            title: 'UYAP oturumu bu hesaba ait değil',
                            text: 'Hesabınızda kayıtlı TC kimlik numarası ile açık olan UYAP ' +
                                  'oturumunun TC kimlik numarası farklı olduğu için işlem yapılamaz. ' +
                                  'Kendi hesabınızla giriş yapıp kendi e-imzanızı kullanın.',
                            icon: 'error',
                            allowOutsideClick: false,
                            confirmButtonColor: '#3085d6',
                            confirmButtonText: 'Tamam'
                        });
                        return;
                    }

                    app.$socket.client.emit('updateTasks');
                    //TODO Task update i sadece socket uzerinden olmali socket de sadece diger clientlara gonderiyor sorun var. duzeltilip dispatch kalirilacak.
                    await store.dispatch('updateTasks');
                    commit('setImzaConnection', true);
                    commit('setUyapConnection', true);
                }
            } catch (error) {
                console.log("checkUyapSession error",error)
                commit('setImzaConnection', false);
                commit('setUyapConnection', false);
            }
        },
        async getKurlarMerkezBankasi({ state, commit }) {
            try {
                if (state.kurlar.length > 0)
                    return state.kurlar;
                let response = await Vue.prototype.$http.get("https://www.icrapro.com/todaykur.php");
                commit('setKurlar', response);
            } catch (e) {
                throw e;
            }
        },
        async getTenantList({ state, commit }) {
            try {
                console.log("getTenantList TRIGGERED");

                // 1) Eğer data zaten varsa direkt dön
                if (state.tenantList.length > 0) {
                    return state.tenantList;
                }

                // 2) Eğer istek devam ediyorsa onu beklet
                if (state.tenantListPromise) {
                    return await state.tenantListPromise;
                }

                // 3) Yeni bir promise yarat
                const promise = Vue.prototype.$http
                    .get("/api/v1/oauth/user-tenant-list")
                    .then(res => {
                        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
                        commit('setTenantList', list);
                        return list;
                    })
                    .finally(() => {
                        commit('setTenantListPromise', null);
                    });

                // Promise’i state’e kaydet
                commit('setTenantListPromise', promise);

                // İlk çağıran bunu bekler
                return await promise;

            } catch (e) {
                commit('setTenantListPromise', null);
                throw e;
            }
        },
        async getSelectedTenantId({state, commit}) {
            return sessionStorage.getItem("tenantId");
        },
        /**
         * DEV-2113 — /ben'i yeniden çeker; kural özeti tazelenir ve bir sonraki geçiş
         * anı için zamanlayıcı yeniden kurulur. Aynı anda tek istek gider.
         * `enAzAralikMs` verilirse son tazelemeden o kadar geçmeden istek atılmaz
         * (lisans 403'ü, bayat özet): tarayıcı saati kaymışsa ekran durmadan istemesin.
         */
        async ozellikDurumunuTazele({state, commit, dispatch}, secenek?: {enAzAralikMs?: number}) {
            if (secenek?.enAzAralikMs && Date.now() - sonOzellikTazelemesi < secenek.enAzAralikMs) {
                return state.user;
            }
            if (!ozellikTazelemeIstegi) {
                sonOzellikTazelemesi = Date.now();
                ozellikTazelemeIstegi = app.$http.get("/api/v1/ben")
                    .then((user: any) => {
                        commit('setUser', user);
                        dispatch('ozellikTazelemesiniKur');
                        return user;
                    })
                    .finally(() => {
                        ozellikTazelemeIstegi = null;
                    });
            }
            return ozellikTazelemeIstegi;
        },
        /** Kural özetinin bayatlayacağı anda /ben'i yeniden çekecek zamanlayıcıyı kurar (öncekini siler). */
        ozellikTazelemesiniKur({state, dispatch}) {
            if (ozellikTazelemeZamanlayici) clearTimeout(ozellikTazelemeZamanlayici);
            ozellikTazelemeZamanlayici = null;
            // Date.now üzerinden: saat testte sabitlenebilsin (jest 24'te Date sahtelenmez).
            const gecikme = tazelemeGecikmesi(state.user?.ozellik_durumu, new Date(Date.now()));
            if (gecikme !== null) {
                ozellikTazelemeZamanlayici = setTimeout(() => {
                    ozellikTazelemeZamanlayici = null;
                    dispatch('ozellikDurumunuTazele').catch(() => undefined);
                }, gecikme);
            }
        },
        async getUser({state, commit}) {
            if (!state.user.id) {
                let response = await Vue.prototype.$http.get("/api/v1/ben");
                commit('setUser', response.data);
            }
            return state.user;
        },
        async getAvukatlar({state, commit}) {
            if (state.avukatlar.length == 0) {
                let response = await app.$http.get("/api/v1/personel/filter?type=avukat");
                commit('setAvukatlar', response.data);
            }
            return state.avukatlar;
        },
        async getMahkemeler({state, commit}) {
            if (state.mahkemeler.length == 0) {
                let response = await app.$http.get('/api/v1/mahkeme/');
                commit('setMahkemeler', response.data);
            }
            return state.mahkemeler;
        },
        async getNoterler({state, commit}) {
            if (state.noterler.length == 0) {
                let response = await app.$http.get('/api/v1/noter/');
                commit('setNoterler', response.data);
            }
            return state.noterler;
        },
        async getPersoneller({state, commit}) {
            if (state.personeller.length == 0) {
                let response = await app.$http.get("/api/v1/personel");
                commit('setPersoneller', response.data)
            }
            return state.personeller;
        },
        async loginWithSso({state, commit}) {
            Vue.prototype.$http.get('/api/v1/oauth/login').then((res: any) => {
                location.href = res.data;
            })
        },
        async tenantData({state,dispatch}){
            return new Promise(async (resolve, reject)=>{
                await dispatch('getTenantList');
                resolve(state.tenantList.find(item => item.tenant_id == state.tenant_id));
            });
        }
    },
    mutations: {
        setIcraProTakipBilgileri(state, data) {
            state.icraProTakipBilgileri = data;
        },
        setRouter(state: AppState, router: VueRouter) {
            state.router = router;
        },
        setCardList(state: AppState, cardList: any[]) {
            state.cardList = cardList;
        },
        setUser(state: AppState, user: PersonelEntity) {
            state.user = user;
            sessionStorage.setItem("user", JSON.stringify(user));
        },
        setTenantId(state: AppState, tenantId: string) {
            sessionStorage.setItem("tenantId", tenantId);
            let tenant = state.tenantList.find(tenant=>tenant.tenant_id == tenantId);
            if(tenant)
                sessionStorage.setItem("selectedTenant", JSON.stringify(tenant));
            state.tenant_id = tenantId;
        },
        setAccessToken(state: AppState, accessToken: string) {
            state.accessToken = accessToken;
            localStorage.setItem("accessToken", accessToken);
        },
        setActiveProcess(state: AppState, activeProcess: any) {
            if (activeProcess.isRoot) {
                state.activeProcesses = [];
            }
            state.activeProcesses.push(activeProcess);
            sessionStorage.setItem("activeProcesses", JSON.stringify(state.activeProcesses))
        },
        setImzaConnection(state: AppState, connection: boolean) {
            state.imzaConnection = connection;
        },
        setUyapConnection(state: AppState, payload: boolean) {
            state.uyapConnection = payload;
        },
        setUyapAvukatBilgileri(state: AppState, avukat: KullaniciBilgileriCevap) {
            state.uyapAvukatBilgileri = avukat;
        },
        setUyapAvukatBakiye(state: AppState, bakiye: string) {
            state.uyapAvukatBakiye = bakiye;
        },
        setUyapSorguBakiye(state: AppState, bakiye: string) {
            state.uyapSorguBakiye = bakiye;
        },
        setAvukatlar(state: AppState, data) {
            state.avukatlar = data;
        },
        setMahkemeler(state: AppState, data) {
            const sorted = Array.isArray(data) ? sortMahkemelerByIsimAsc(data) : [];
            state.mahkemeler.splice(0, state.mahkemeler.length, ...sorted);
        },
        resortMahkemeler(state: AppState) {
            const sorted = sortMahkemelerByIsimAsc(state.mahkemeler);
            state.mahkemeler.splice(0, state.mahkemeler.length, ...sorted);
        },
        setNoterler(state: AppState, data) {
            state.noterler = data;
        },
        setTenantList(state: AppState, data) {
            state.tenantList = data;
        },
        setKurlar(state: AppState, data) {
            state.kurlar = data;
        },
        setTenantListPromise(state: AppState, promise) {
            state.tenantListPromise = promise;
        },
        setPersoneller(state: AppState, data) {
            state.personeller = data;
        },
        setImzaConnIntervalId(state: AppState, intervalId) {
            state.imzaConnIntervalId = intervalId;
        },
    },
    getters: {
        router: (state: AppState) => state.router,
        accessToken: (state: AppState) => state.accessToken,
        isDev: (state: AppState) => !window.location.hostname.includes('.com'),
        isEicraPro: (state: AppState) => window.location.pathname.startsWith(ProgramPaths.eicrapro),
        isIcraPro: (state: AppState) => window.location.pathname.startsWith(ProgramPaths.icrapro),
        isOfisPro: (state: AppState) => window.location.pathname.startsWith(ProgramPaths.ofispro),
        isDavaPro: (state: AppState) => window.location.pathname.startsWith(ProgramPaths.davapro),
        isArabulucuPro: (state: AppState) => window.location.pathname.startsWith(ProgramPaths.arabulucupro),
        isImzaConnected: (state: AppState) => state.imzaConnection,
        isUyapConnected: (state: AppState) => state.uyapConnection,
        home: (state: AppState) => ProgramPaths.ofispro,
        isEicraproDevMode: (state: AppState) => state.eicraproDevMode,
        avukatBilgileri: (state: AppState) => state.uyapAvukatBilgileri,
        avukatBakiye: (state: AppState) => state.uyapAvukatBakiye,
        sorguBakiye: (state: AppState) => state.uyapSorguBakiye,
        ssoConfig: (state: AppState) => state.ssoConfig,
        userData: (state: AppState) => state.user,
        // Ürün-bazlı yenileme teklifi. Parametreli getter: Vuex memoize etmez,
        // bileşen anahtarı reaktif $route.path'ten türetip verir (window.location
        // Vuex getter'ında reaktif olmadığından ürün geçişinde bayat kalıyordu).
        //
        // KAYNAK: /api/v1/ben yanıtındaki `lisans_durum` özeti. Ayrı bir
        // /renewal-offer isteği ATILMAZ — özet zaten kullanıcıyla birlikte
        // sessionStorage'da durduğu için sayfa yenilemesi sıfır istek eder.
        // Kademe/gün hesabı burada yapılır ki gece yarısı bayatlamasın.
        renewalOfferFor: (state: AppState) => (key: 'icrapro' | 'eicrapro'): any => {
            const durum: any = (state.user as any)?.lisans_durum;
            if (!durum) return null;
            return offerFromLisansDurum(durum[key]);
        },
        // Ürün-bazlı demo durumu. renewalOfferFor ile aynı desen ve aynı kaynak.
        demoStatusFor: (state: AppState) => (key: 'icrapro' | 'eicrapro'): any => {
            const durum: any = (state.user as any)?.lisans_durum;
            if (!durum) return null;
            return demoStatusFromLisansDurum(durum[key]);
        },
        licenses: (state: AppState) => state.user.licenses.filter(licence => licence.tenant_id == state.tenant_id),
        // DEV-2113 — /ben kural özeti; yoksa null (ekran kısıtı göstermez, sunucu engeller).
        ozellikDurumu: (state: AppState) => state.user?.ozellik_durumu ?? null,
        tenant: (state: AppState) => state.tenantList.find(item => item.tenant_id == state.tenant_id)
    }
});
